"use client";
import some from 'lodash/some';
import { useCallback } from 'react';
import { useRouter } from './useRouter2.js';

const useWebNavigationUtility = ({ currentUserActions, isLoggedInUser }, routesPermissions, basePermissionLevels, isNavigationInProgressUrl, router) => {
    const { replace, push, query, back, pathname, locale } = useRouter(router);
    const getNavigatingRouteState = useCallback((...routes) => {
        const isNavigatingState = [];
        const navigatingRoute = isNavigationInProgressUrl ? getRouteForPath(isNavigationInProgressUrl) : undefined;
        routes.forEach((route) => {
            isNavigatingState.push(navigatingRoute === route);
        });
        return isNavigatingState;
    }, [isNavigationInProgressUrl]);
    const getPathForRoute = useCallback((route) => {
        return routesPermissions[route].path;
    }, [routesPermissions]);
    const getRouteForPath = useCallback((path) => {
        if (router === 'app') {
            // remove lng from the url
            if (path.startsWith(`/${locale}/`)) {
                path = path.replace(`/${locale}`, '');
            }
        }
        const comparePaths = (template, path) => {
            const templateArray = template.split('/');
            const pathArray = path.split('/');
            if (templateArray.length !== pathArray.length)
                return false;
            return templateArray.every((templateRoute, i) => {
                if (templateRoute.startsWith('[') && templateRoute.endsWith(']'))
                    return true;
                return templateRoute === pathArray[i];
            });
        };
        const pagesKeys = Object.keys(routesPermissions);
        const pageKeyIndex = router === 'app'
            ? pagesKeys.findIndex((pageKey) => comparePaths(routesPermissions[pageKey].path, path))
            : pagesKeys.findIndex((pageKey) => routesPermissions[pageKey].path === path);
        if (pageKeyIndex > -1) {
            return pagesKeys[pageKeyIndex];
        }
        return undefined;
    }, [routesPermissions]);
    const userHasPermissionOnScreen = useCallback((...args) => {
        const [route, currentUserPermissions, isLoggedIn] = args;
        const requiredScreenPermissions = routesPermissions[route].permissions;
        return some(requiredScreenPermissions, (permissionLevel) => (currentUserPermissions === null || currentUserPermissions === void 0 ? void 0 : currentUserPermissions.includes(basePermissionLevels.superAdmin)) ||
            (currentUserPermissions === null || currentUserPermissions === void 0 ? void 0 : currentUserPermissions.includes(permissionLevel)) ||
            (isLoggedIn && permissionLevel === basePermissionLevels.allLoggedInUser) ||
            (!isLoggedIn && permissionLevel === basePermissionLevels.notLoggedInUser) ||
            permissionLevel === basePermissionLevels.anonymousUser);
    }, [routesPermissions]);
    const navigateToInPageRouter = useCallback(async (...args) => {
        const [route, query, isReplace, options] = args;
        const routerNavigate = isReplace ? replace : push;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const filteredQuery = Object.fromEntries(Object.entries(query || {}).filter(([_, value]) => value !== undefined));
        const url = getPathForRoute(route);
        return await routerNavigate(Object.assign({ pathname: url !== null && url !== void 0 ? url : '' }, (Object.keys(filteredQuery).length > 0 ? { query: filteredQuery } : {})), options === null || options === void 0 ? void 0 : options.asPath, { shallow: options === null || options === void 0 ? void 0 : options.shallow });
    }, [getPathForRoute]);
    const navigateToInAppRouter = useCallback(async (...args) => {
        const [route, query, isReplace, options] = args;
        const routerNavigate = isReplace ? replace : push;
        const newRoute = getPathForRoute(route);
        const _query = Object.assign({}, query);
        let _path = newRoute;
        newRoute
            .split('/')
            .filter((r) => r.startsWith('[') && r.endsWith(']'))
            .map((r) => r.replace('[', '').replace(']', ''))
            .forEach((r) => {
            if (_query[r]) {
                _path = _path.replace(`[${r}]`, _query[r]);
                delete _query[r];
            }
        });
        const UrlQuery = new URLSearchParams(_query);
        const url = `${locale !== 'en' ? `/${locale}` : ''}${_path}${UrlQuery.toString() ? `?${UrlQuery.toString()}` : ''}`;
        return await routerNavigate(url, options === null || options === void 0 ? void 0 : options.asPath, { shallow: options === null || options === void 0 ? void 0 : options.shallow });
    }, [getPathForRoute]);
    const navigationPush = useCallback(async (...args) => {
        const [route, query, options] = args;
        return router === 'app'
            ? await navigateToInAppRouter(route, query, false, options)
            : await navigateToInPageRouter(route, query, false, options);
    }, []);
    const navigationReplace = useCallback(async (...args) => {
        const [route, query, options] = args;
        return router === 'app'
            ? await navigateToInAppRouter(route, query, true, options)
            : await navigateToInPageRouter(route, query, true, options);
    }, []);
    const routerQuery = useCallback((
    /** args parameter is required to define the output type  */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    route) => {
        const queryValue = query !== null && query !== void 0 ? query : {};
        Object.keys(queryValue).forEach((key) => {
            const paramValue = queryValue[key];
            queryValue[key] = paramValue;
        });
        return queryValue;
    }, [query]);
    const navigationGoBack = useCallback((pageNum = -1) => {
        if (pageNum && pageNum < -1) {
            window.history.go(pageNum);
        }
        else
            back();
    }, []);
    const CheckIfUserHasPermissionToScreen = useCallback((route) => {
        return userHasPermissionOnScreen(route, currentUserActions, isLoggedInUser);
    }, [userHasPermissionOnScreen]);
    const getCurrentPage = useCallback(() => {
        return getRouteForPath(pathname);
    }, [getRouteForPath, pathname]);
    const CheckIfUserHasPermissionOnCurrentPage = useCallback(() => {
        const currentScreen = getCurrentPage();
        if (currentScreen)
            return userHasPermissionOnScreen(currentScreen, currentUserActions, isLoggedInUser);
        return false;
    }, [userHasPermissionOnScreen, getCurrentPage, isLoggedInUser, currentUserActions]);
    const CheckIfUserHasAccessOnCurrentPage = useCallback(() => {
        var _a;
        const currentScreen = getCurrentPage();
        return (_a = (currentScreen && routesPermissions[currentScreen].permissions.length !== 0)) !== null && _a !== void 0 ? _a : false;
    }, [userHasPermissionOnScreen, getCurrentPage, isLoggedInUser, currentUserActions]);
    const CheckIfCurrentPageIsNotLoggedInOnly = useCallback(() => {
        const currentScreen = getCurrentPage();
        if (!currentScreen)
            return false;
        const requiredScreenPermissions = routesPermissions[currentScreen].permissions;
        return (isLoggedInUser &&
            some(requiredScreenPermissions, (permissionLevel) => permissionLevel === basePermissionLevels.notLoggedInUser));
    }, [getCurrentPage, isLoggedInUser, routesPermissions]);
    return {
        navigationPush,
        navigationReplace,
        navigationGoBack,
        routerQuery,
        getPathForRoute,
        CheckIfUserHasPermissionToScreen,
        CheckIfUserHasPermissionOnCurrentPage,
        CheckIfUserHasAccessOnCurrentPage,
        CheckIfCurrentPageIsNotLoggedInOnly,
        getCurrentPage,
        getNavigatingRouteState,
    };
};

export { useWebNavigationUtility };
